import { hrefOf } from '../overview/href'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  agentAssignmentQuery,
  agentDeviceSettingsQuery,
  saveAgentAppearance,
  saveAgentAssignment,
  type AgentSettingsTab,
  type DeviceAgentAppearancePage,
  type DeviceAgentAssignmentPage,
} from '../api/devices'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'

/**
 * Fila Settings - deviceSettings.php: banda managementList (Agent Appearance /
 * Assignment). Appearance e agentAppearance.php (cele doua iCheck). Assignment
 * e agentAssignment.php (User / Area / Office / Group / Classified / Auto update).
 */

export function DeviceAgentDeviceSettings({ id }: { id: number }) {
  const page = useQuery(agentDeviceSettingsQuery(id))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <Band page={page.data} />
}

function Band({ page }: { page: DeviceAgentAppearancePage }) {
  const first = page.tabs[0]?.key ?? 'appearance'
  const [active, setActive] = useState(first)
  const current = page.tabs.find((t) => t.key === active) ?? page.tabs[0]

  return (
    <div className="row">
      <div className="col-md-12">
        <ul className="nav nav-tabs line-tabs mb-2 managementList" role="tablist">
          {page.tabs.map((tab) => (
            <li key={tab.key} className={tab.key === active ? 'active' : undefined}>
              <a
                href={hrefOf(`${tab.href}${page.device.id}`) ?? '#'}
                onClick={(e) => {
                  e.preventDefault()
                  setActive(tab.key)
                }}
              >
                <i className={tab.icon}></i> {tab.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {current?.key === 'appearance' ? (
            <Appearance page={page} />
          ) : current?.key === 'assignment' ? (
            <Assignment id={page.device.id} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Appearance({ page }: { page: DeviceAgentAppearancePage }) {
  const query = useQueryClient()
  const write = async (status: string) => {
    await saveAgentAppearance(page.device.id, status)
    toast(status.includes('desktop') ? 'Settings saved!' : 'Module saved!', 'Success')
    await query.invalidateQueries({ queryKey: ['device', page.device.id, 'agent-settings', 'settings'] })
  }

  return (
    <table className="table table-hover">
      <tbody>
        <tr style={{ background: '#eee' }}>
          <td className="a-left">
            <strong>Create desktop shortcut</strong>
          </td>
          <td style={{ textAlign: 'center' }}>
            <SettingsCheck
              name="desktop_shortcut"
              aria-label="Create desktop shortcut"
              data-device={page.device.id}
              className="iCheckM iCheckDesktopShortcut"
              value="1"
              checked={page.appearance.desktopShortcut}
              onChange={(on) => void write(on ? 'enable_desktop_shortcut' : 'disable_desktop_shortcut')}
            />
          </td>
        </tr>
        <tr style={{ background: '#ddd' }}>
          <td className="a-left">
            <strong>Show Application Icon on Toolbar</strong>
          </td>
          <td style={{ textAlign: 'center' }}>
            <SettingsCheck
              name="application_icon"
              aria-label="Show Application Icon on Toolbar"
              data-device={page.device.id}
              className="iCheckM iCheckAppIcon"
              value="1"
              checked={page.appearance.applicationIcon}
              onChange={(on) => void write(on ? 'enabled_application_icon' : 'disabled_application_icon')}
            />
          </td>
        </tr>
      </tbody>
    </table>
  )
}

function Assignment({ id }: { id: number }) {
  const page = useQuery(agentAssignmentQuery(id))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <AssignmentForm page={page.data} />
}

function AssignmentForm({ page }: { page: DeviceAgentAssignmentPage }) {
  const query = useQueryClient()
  const [owner, setOwner] = useState(page.owner.id > 0 ? String(page.owner.id) : '')
  const [ownerName, setOwnerName] = useState(page.owner.name)
  const [area, setArea] = useState(page.areaId > 0 ? String(page.areaId) : '')
  const [office, setOffice] = useState(page.officeId > 0 ? String(page.officeId) : '')
  const [group, setGroup] = useState(page.groupId > 0 ? String(page.groupId) : '')
  const [classified, setClassified] = useState(page.classified)
  const [autoUpdate, setAutoUpdate] = useState(page.autoUpdate)
  const [beta, setBeta] = useState(page.beta)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!window.confirm('Are you sure?')) return
    setBusy(true)
    try {
      await saveAgentAssignment(page.device.id, {
        device_owner: owner,
        device_area: area,
        device_office: office,
        device_group: group,
        device_classified_info: classified,
        agent_auto_update: autoUpdate ? 1 : 0,
        agent_beta_version: beta ? 1 : 0,
      })
      toast('Settings saved!', 'Success')
      await query.invalidateQueries({ queryKey: ['device', page.device.id, 'agent-settings', 'assignment'] })
    } catch {
      toast('Please try again.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="panel-body">
      <form
        className="form-horizontal labelcustomize updateDeviceSettings"
        data-collabel="3"
        data-alignlabel="left"
        data-label="color"
        method="post"
        action="/overview/updateSettings"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <input type="hidden" className="form-control" name="device_unique" value={page.device.id} required />
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">User</span>
          </label>
          <div className="col-sm-9">
            <div className="row">
              <div className="col-md-9">
                <select
                  name="device_owner"
                  className="form-control owner_list_autocomplete"
                  value={owner}
                  onChange={(e) => {
                    const opt = e.target.selectedOptions[0]
                    setOwner(e.target.value)
                    setOwnerName(opt?.text ?? '')
                  }}
                >
                  {owner ? <option value={owner}>{ownerName || page.owner.name}</option> : <option value="">Select...</option>}
                  {page.owners
                    .filter((o) => String(o.id) !== owner)
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.text}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-md-3">
                <button
                  id="deleteuserassignee"
                  type="button"
                  className="btn btn-danger pull-right"
                  onClick={() => {
                    setOwner('')
                    setOwnerName('')
                  }}
                >
                  <i className="fa fa-times"></i> Delete user
                </button>
              </div>
            </div>
            <span className="help-block">
              <i className="fas fa-info-circle"></i> If user is not selected the device goes to unassigned
            </span>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Area</span>
          </label>
          <div className="col-sm-9">
            <select name="device_area" className="form-control" value={area} onChange={(e) => setArea(e.target.value)}>
              <option value="">Select...</option>
              {page.areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Office</span>
          </label>
          <div className="col-sm-9">
            <select name="device_office" className="form-control" required value={office} onChange={(e) => setOffice(e.target.value)}>
              <option value="">Select...</option>
              {page.offices.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Group</span>
          </label>
          <div className="col-sm-9">
            <select name="device_group" className="form-control" value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">Select...</option>
              {page.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Classified info</span>
          </label>
          <div className="col-sm-9">
            <textarea
              style={{ maxWidth: '100%' }}
              className="form-control"
              name="device_classified_info"
              value={classified}
              onChange={(e) => setClassified(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Agent install settings</span>
          </label>
          <div className="col-sm-9">
            <div style={{ display: 'flex', gap: 50, paddingTop: 7 }}>
              <SettingsCheck
                id="inlineCheckbox1"
                name="agent_auto_update"
                value="1"
                className="iCheckSettings"
                checked={autoUpdate}
                onChange={setAutoUpdate}
                label="Auto update"
              />
              <SettingsCheck
                name="agent_beta_version"
                value="1"
                className="iCheckSettings"
                checked={beta}
                onChange={setBeta}
                label="Beta version"
              />
            </div>
          </div>
        </div>
        <div className="form-group offset">
          <div className="col-md-offset-3 col-md-9">
            <button type="submit" className="btn btn-theme" disabled={busy}>
              Update
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export function SettingsBadge({ tab }: { tab: AgentSettingsTab }) {
  if (tab.badge === null || tab.badge === '') return null
  return (
    <span className={`tab-badge tab-badge-${tab.badgeTone}`} title={tab.badgeTitle}>
      {tab.badge}
    </span>
  )
}
